import {envIsPublicCloud} from '../config';

/**
 * These published-work paths should be kept for backwards compatibility. This was a quick hack job
 * (before gallery) to satisfy a few customers who wanted to publicize reports while using data from
 * a private project. The way to create such a report is via a very old script that (hopefully) isn't
 * being used anymore. This should be phased out in the future, but there are a few reports that still
 * utilize this path (eg. openai).
 */
export const PUBLISHED_WORK_PATH_SEGMENT = 'published-work';
export const REPORT_PAGE_VIEW_PUBLISHED_WORK_PATH = `/:entityName/${PUBLISHED_WORK_PATH_SEGMENT}/:reportNameAndID`;

/**
 * These gallery paths are "old fully connected" paths that should be kept for backwards compatibility.
 * Ideally, this should be phased out. Gallery and FC are terms that are used all over the codebase,
 * so the terminology also needs to be updated.
 */

// This variable name is quite confusing. We should rename it to be FULLY_CONNECTED_SEGMENT
// so it matches the redirecting and file naming logic.
export const GALLERY_PATH_SEGMENT = 'fully-connected';

export const GALLERY_PATH = [
  `/${GALLERY_PATH_SEGMENT}/projects/:repoName`,
  `/${GALLERY_PATH_SEGMENT}/topics/:topic`,
  `/${GALLERY_PATH_SEGMENT}/:page?/:tag?/:reportID?`,
];

// Only GALLERY_ADMINS have access to this internal wandb admins and manage fully connected content
// including pending posts, discussions, content ordering, etc.
export const GALLERY_INTERNAL_ADMIN_PATH = `/admin/${GALLERY_PATH_SEGMENT}`;

/**
 * @deprecated - this is no longer a valid path, but keeping it around for historical context
 * This path was used when clicking on "Write post" in fully connected
 */
export const GALLERY_CREATE_POST = `/${GALLERY_PATH_SEGMENT}/create-post`;
/**
 * @deprecated - this is no longer a valid path, but keeping it around for historical context
 * This path was used when clicking on "Write post" in fully connected
 */
export const GALLERY_POST_VIEW_PATH = '/:entityName/posts/:reportNameAndID';
/**
 * @deprecated - this is no longer a valid path, but keeping it around for historical context
 * This path was used when clicking on "Write post" in fully connected
 */
export const GALLERY_POST_EDIT_PATH = `${GALLERY_POST_VIEW_PATH}/edit`; // redirects to view mode

export const REPORT_PAGE_VIEW_PATH =
  '/:entityName/:projectName/reports/:reportNameAndID';
export const REPORT_PAGE_EDIT_PATH = `${REPORT_PAGE_VIEW_PATH}/edit`;

// Deprecated report paths that redirect to /reportlist now
export const OLD_REPORT_PAGE_VIEW_PATH = '/:entityName/:projectName/published';
export const OLD_REPORT_PAGE_EDIT_PATH = '/:entityName/:projectName/reports';

// Old featured report paths that go through the fully connected paths now because
// there is no way to trigger create-discussion anymore
export const OLD_GALLERY_DISCUSSION_VIEW_PATH =
  '/:entityName/discussions/:reportNameAndID';
export const OLD_GALLERY_DISCUSSION_EDIT_PATH = `${OLD_GALLERY_DISCUSSION_VIEW_PATH}/edit`; // redirects to view mode

export const STORAGE_EXPLORER_TRACKED_PATH =
  '/usage/:entityName/tracked/:filePath*';
export const STORAGE_EXPLORER_COMPUTE_HOURS_PATH =
  '/usage/:entityName/computehour';
export const USAGE_PATH = '/usage/:entityName';

export const ORG_DASHBOARD = '/org/dashboard';
export const ORG_ROLES_NEW = envIsPublicCloud
  ? '/account-settings/:orgName/:tab/roles/new'
  : '/org/settings/roles/new';
export const ORG_ROLE_EDIT = envIsPublicCloud
  ? '/account-settings/:orgName/:tab/roles/:roleId/edit'
  : '/org/settings/roles/:roleId/edit';
export const ORG_PROXY = '/org/proxy';
export const ORG_TEAMS = '/org/teams';
export const ORG_USER_ACTIVITY = '/org/activity';
export const ORG_SERVICE_ACCOUNTS = '/org/service-accounts';
export const ORG_BILLING = '/org/billing';
export const ORG_SETTINGS = envIsPublicCloud
  ? '/account-settings/:orgName/settings'
  : '/org/settings';

export const ADMIN_GLUE = '/admin/glue';
export const ADMIN_ORGANIZATION = [
  '/admin/organization',
  '/admin/organizations',
];

export const ORG_SETTINGS_PATHS = [ORG_SETTINGS, ORG_ROLES_NEW, ORG_ROLE_EDIT];

export const ACCOUNT_SETTINGS_USAGE_PAGE_PATHS = [
  '/account-settings/:orgName/usage/manage',
  '/account-settings/:orgName/usage/manage/:entityName/:filePath*',
];

export const ACCOUNT_SETTINGS_PATHS = [
  '/billing',
  '/subscriptions',
  '/account-settings',
  '/account-setting',
  '/account-settings/:orgName',
  '/account-setting/:orgName',
  '/account-settings/:orgName/:tab',
  '/account-setting/:orgName/:tab',
  '/account-settings/:orgName/:tab/roles/new',
  '/account-settings/:orgName/:tab/roles/:roleId/edit',
];

export const HOME_PATH = '/home';
export const USER_SETTINGS_PATH = '/settings';
export const PROFILE_PATH = '/:entityName';
export const PROFILE_REPORTS_PATH = `${PROFILE_PATH}/reports`;
export const PROFILE_PROJECTS_PATH = `${PROFILE_PATH}/projects`;
export const PROFILE_STARS_PATH = `${PROFILE_PATH}/stars`;
export const TEAM_MEMBERS_PATH = `${PROFILE_PATH}/members`;
export const TEAM_SERVICE_ACCOUNTS_PATH = `${PROFILE_PATH}/service-accounts`;
export const TEAM_SETTINGS_PATH = `${PROFILE_PATH}/settings`;
// NOTE: These paths are risky since they overlap with the
// schema of PROJECT_BASE_PATH. It would be nice to always
// mediate variable route components with a static path. For
// example: `/:entityName/project/:projectName` instead of
// `/:entityName/:projectName`.

// Team-level registry paths
export const ENTITY_REGISTRY_ALL_TEAMS_PATHS = [
  '/teams/registry/',
  '/teams/registry/:registryName?',
];
export const ENTITY_REGISTRY_ACCESS_CONTROL_PATH =
  '/:entityName/registry/:registryName/access-control';
export const ENTITY_REGISTRY_PATHS = [
  ...ENTITY_REGISTRY_ALL_TEAMS_PATHS,
  '/:entityName/registry/',
  '/:entityName/registry/:registryName?',
  ENTITY_REGISTRY_ACCESS_CONTROL_PATH,
];

// Organization registry paths
// Deprecated registry paths that redirect to /orgs/:orgName/registry/:registryName now
export const OLD_ORG_REGISTRY_SETTINGS_PATH =
  '/registry/:registryName/settings';
export const OLD_ENTITY_REGISTRY_ALL_TEAMS_PATHS = [
  '/registry/',
  '/registry/:registryName?',
  OLD_ORG_REGISTRY_SETTINGS_PATH,
];
export const ORG_SCOPED_REGISTRY_SETTINGS_PATH = envIsPublicCloud
  ? '/orgs/:orgName/registry/:registryName/settings'
  : '/orgs/server/registry/:registryName/settings';
export const ORG_SCOPED_REGISTRY_PATHS = envIsPublicCloud
  ? ['/orgs/:orgName/registry/', '/orgs/:orgName/registry/:registryName?']
  : ['/orgs/server/registry/', '/orgs/server/registry/:registryName?'];
export const ORG_REGISTRY_PATHS = [
  ...OLD_ENTITY_REGISTRY_ALL_TEAMS_PATHS,
  ...ORG_SCOPED_REGISTRY_PATHS,
  ORG_SCOPED_REGISTRY_SETTINGS_PATH,
];
export const REGISTRY_PATHS = [...ENTITY_REGISTRY_PATHS, ...ORG_REGISTRY_PATHS];

export const ENTITY_LAUNCH_PATHS = [
  '/launch/',
  '/:entityName/launch',
  '/launch/agents',
  '/:entityName/launch/agents',
  '/launch/resources',
  '/:entityName/launch/resources',
];
export const AGENT_PATHS = [
  '/:entityName/launch/agents/:agentId',
  '/:entityName/launch/agents/:agentId/:tab',
];
export const QUEUE_PATHS = [
  '/:entityName/launch/:queueId',
  '/:entityName/launch/:queueId/:tab',
];

export const PROJECT_BASE_PATH = '/:entityName/:projectName';
export const RUN_BASE_PATH = '/:entityName/:projectName/runs/:runName';
export const QUICKSTART_PATH = '/quickstart';
export const ONBOARDING_FLOW_PATH = `/signup`;

export const WEAVE_TITLE = 'Weave';
export const WEAVE_SLUG = 'weave';
export const PROJECT_WEAVE_PATHS = [
  `/:entityName/:projectName/:tab(${WEAVE_SLUG})/:weaveTab/:itemName/versions/:version/:refExtra*`,
  `/:entityName/:projectName/:tab(${WEAVE_SLUG})/:weaveTab/:itemName`,
  `/:entityName/:projectName/:tab(${WEAVE_SLUG})/:weaveTab`,
  `/:entityName/:projectName/:tab(${WEAVE_SLUG})`,
];

export const FULL_SCREEN_WORKSPACE_PANEL_PATH =
  '/:entityName/:projectName/panel/:panelId';
export const FULL_SCREEN_WORKSPACE_TAB_PANEL_PATH =
  '/:entityName/:projectName/:tab(workspace)/panel/:panelId';
export const FULL_SCREEN_RUNS_PANEL_PATH =
  '/:entityName/:projectName/runs/:runName/panel/:panelId';
export const FULL_SCREEN_RUNS_WORKSPACE_PANEL_PATH =
  '/:entityName/:projectName/runs/:runName/:runTab(workspace)/panel/:panelId';
export const FULL_SCREEN_GROUP_PANEL_PATH =
  '/:entityName/:projectName/groups/:groupName/panel/:panelId';
export const FULL_SCREEN_GROUP_WORKSPACE_TAB_PANEL_PATH =
  '/:entityName/:projectName/groups/:groupName/:tab(workspace)/panel/:panelId';
export const FULL_SCREEN_GROUP_RUN_PANEL_PATH =
  '/:entityName/:projectName/groups/:groupName/runs/:runName/panel/:panelId';
export const FULL_SCREEN_SWEEP_PANEL_PATH =
  '/:entityName/:projectName/sweeps/:sweepName/panel/:panelId';
export const FULL_SCREEN_SWEEP_WORKSPACE_TAB_PANEL_PATH =
  '/:entityName/:projectName/sweeps/:sweepName/:tab(workspace)/panel/:panelId';
export const FULL_SCREEN_SWEEP_RUN_PANEL_PATH =
  '/:entityName/:projectName/sweeps/:sweepName/runs/:runName/panel/:panelId';
export const EMBEDDED_WORKPACE_PANEL_PATH =
  '/:entityName/:projectName/embed/panel/:panelId';
export const EMBEDDED_RUNS_PANEL_PATH =
  '/:entityName/:projectName/runs/:runName/embed/panel/:panelId';
export const EMBEDDED_GROUP_PANEL_PATH =
  '/:entityName/:projectName/groups/:groupName/embed/panel/:panelId';
export const EMBEDDED_SWEEP_PANEL_PATH =
  '/:entityName/:projectName/sweeps/:sweepName/embed/panel/:panelId';

export const RUNS_TAB_PATH = '/:entityName/:projectName/runs/:runName/:runTab';
export const RUN_WORKSPACE_PATH =
  '/:entityName/:projectName/runs/:runName/:runTab(workspace)';
export const RUN_FILES_PATH =
  '/:entityName/:projectName/runs/:runName/:runTab(files)/:filePath*';
export const RUN_CODE_FILES_PATH =
  '/:entityName/:projectName/runs/:runName/:runTab(code)/:filePath*';
export const RUN_WORKSPACE_PATHS = [
  RUN_BASE_PATH,
  RUN_WORKSPACE_PATH,
  FULL_SCREEN_RUNS_PANEL_PATH,
  FULL_SCREEN_RUNS_WORKSPACE_PANEL_PATH,
  EMBEDDED_RUNS_PANEL_PATH,
];
export const RUN_PATHS = [
  ...RUN_WORKSPACE_PATHS,
  RUN_FILES_PATH,
  RUN_CODE_FILES_PATH,
  RUNS_TAB_PATH,
];

/**
 * PROJECT_BASE_PATH also lands on the the project workspace,
 * and is actually the more commonly used URL for it. This
 * `/workspace` subpath is extraneous, but has been around for a
 * long time and may be saved/bookmarked/shared by users,
 * so we must keep it for legacy purposes.
 */
export const PROJECT_WORKSPACE_PATH =
  '/:entityName/:projectName/:tab(workspace)';
export const PROJECT_WORKSPACE_PATHS = [
  PROJECT_BASE_PATH,
  PROJECT_WORKSPACE_PATH,
  FULL_SCREEN_WORKSPACE_PANEL_PATH,
  FULL_SCREEN_WORKSPACE_TAB_PANEL_PATH,
  EMBEDDED_WORKPACE_PANEL_PATH,
];
export const PROJECT_PATHS = [
  ...PROJECT_WORKSPACE_PATHS,
  '/:entityName/:projectName/:tab(overview|table|reportlist|sweeps|launch|jobs|automations|artifacts)',
  '/:entityName/:projectName/:tab(artifacts)/:artifactTypeName(new)',
  '/:entityName/:projectName/:tab(artifacts)/:artifactTypeName',
  '/:entityName/:projectName/:tab(artifacts)/:artifactTypeName/:artifactCommitHash(new)',
  '/:entityName/:projectName/:tab(artifacts)/:artifactTypeName/:artifactCollectionName/_view/:artifactCollectionView',
  '/:entityName/:projectName/:tab(artifacts)/:artifactTypeName/:artifactCollectionName/:artifactCommitHash/:artifactTab(files)/:filePath*',
  '/:entityName/:projectName/:tab(artifacts)/:artifactTypeName/:artifactCollectionName/:artifactCommitHash/:artifactTab?',
  '/:entityName/:projectName/:tab(artifacts)/:artifactTypeName/:artifactCollectionName',
  '/:entityName/:projectName/:tab(overview)/:tabName(details|users|providers)',
  '/:entityName/:projectName/:tab(launch)/:launchTab?',
  '/:entityName/:projectName/:tab(jobs)/:jobId',
  '/:entityName/:projectName/:tab(jobs)/:jobId/:jobTab(runs|job_details|version_details)/:jobVersion?',
  ...PROJECT_WEAVE_PATHS,
];
/**
 * for consolidated workspaces, run paths are nested under project page.
 * this is currently feature flagged, but after rollout we'll remove the flag
 * and spread RUN_PATHS into PROJECT_PATHS directly.
 */
export const PROJECT_PATHS_FOR_CONSOLIDATED_WORKSPACES = [
  ...PROJECT_PATHS,
  ...RUN_PATHS,
];

export const REDIRECT_PATH = '/:entityName/:projectName/r/:redirectPath*';

export const ARTIFACT_VERSION_PATH =
  '/:entityName/:projectName/artifacts/:artifactTypeName/:artifactCollectionName/:artifactCommitHash';

export const GROUP_BASE_PATH = '/:entityName/:projectName/groups/:groupName';
export const GROUP_WORKSPACE_PATH =
  '/:entityName/:projectName/groups/:groupName/:tab(workspace)';
export const GROUP_WORKSPACE_PATHS = [
  GROUP_BASE_PATH,
  GROUP_WORKSPACE_PATH,
  FULL_SCREEN_GROUP_PANEL_PATH,
  FULL_SCREEN_GROUP_WORKSPACE_TAB_PANEL_PATH,
  EMBEDDED_GROUP_PANEL_PATH,
  // single-run mode paths
  '/:entityName/:projectName/groups/:groupName/:tab(runs)/:runName',
  '/:entityName/:projectName/groups/:groupName/:tab(runs)/:runName/:runTab',
  FULL_SCREEN_GROUP_RUN_PANEL_PATH,
];
export const GROUP_PATHS = [
  ...GROUP_WORKSPACE_PATHS,
  '/:entityName/:projectName/groups/:groupName/:tab(files)/:filePath*',
  '/:entityName/:projectName/groups/:groupName/:tab',
];

export const SWEEP_BASE_PATH = '/:entityName/:projectName/sweeps/:sweepName';
export const SWEEP_WORKSPACE_PATH =
  '/:entityName/:projectName/sweeps/:sweepName/:tab(workspace)';
export const SWEEP_WORKSPACE_PATHS = [
  SWEEP_BASE_PATH,
  SWEEP_WORKSPACE_PATH,
  FULL_SCREEN_SWEEP_PANEL_PATH,
  FULL_SCREEN_SWEEP_WORKSPACE_TAB_PANEL_PATH,
  EMBEDDED_SWEEP_PANEL_PATH,
  // single-run mode paths
  '/:entityName/:projectName/sweeps/:sweepName/:tab(runs)/:runName',
  '/:entityName/:projectName/sweeps/:sweepName/:tab(runs)/:runName/:runTab',
  FULL_SCREEN_SWEEP_RUN_PANEL_PATH,
];
export const SWEEP_PATHS = [
  ...SWEEP_WORKSPACE_PATHS,
  '/:entityName/:projectName/sweeps/:sweepName/:tab',
];

export const BENCHMARK_PATHS = [
  '/:entityName/:projectName/benchmark',
  '/:entityName/:projectName/benchmark/:tab/:threadID?',
];

export const NO_ACCESS_PATH = '/no-access';

export const ANALYZE_MY_EXPERIMENTS_PATH = '/analyze_my_experiments';

export const TRIAL_END_PATH = '/trial_end';

export const CREATE_TEAM_PATH = '/create-team';

export const ACADEMIC_APPLICATION_PATH = '/academic_application';

export enum UsageTabURLPart {
  TRACKED_HOURS = 'tracked-hours',
  STORAGE = 'storage',
  WEAVE = 'weave',
  INFERENCE_COST = 'inference',
}

export const GLOBAL_HISTORIC_USAGE_REDIRECT_PATH = '/orgs/server/usage';
export const GLOBAL_HISTORIC_USAGE_REDIRECT_TO_PATH = `/orgs/server/usage/${UsageTabURLPart.STORAGE}`;
export const GLOBAL_HISTORIC_USAGE_PATH = '/orgs/server/usage/:tab';

export const HISTORIC_USAGE_REDIRECT_PATH = '/:orgName/usage';
export const HISTORIC_USAGE_REDIRECT_TO_PATH = `/:orgName/usage/${UsageTabURLPart.STORAGE}`;
export const HISTORIC_USAGE_PATH = '/:orgName/usage/:tab';

export const STARTUP_PROGRAM = '/startup-program';

export const INFERENCE_PATH = '/inference';
export const INFERENCE_COMPARE_PATH = '/inference-compare';

export enum UTM_Source {
  FREE_PLAN = 'free-plan',
  APP_RESOURCE_CENTER = 'app-resource-center',
}

export enum UTM_Content {
  LEFT_NAV = 'left_nav',
}

export enum UTM_Medium {
  APP = 'app',
}

export enum UTM_Term {
  DOCS = 'docs',
  QUICKSTART = 'quickstart',
  FULLY_CONNECTED = 'fully-connected',
}
